import { Cell } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import CheckPermission from 'components/auth/CheckPermission'
import { usePecField } from 'components/form/core/hooks/useField'
import React from 'react'
import Permissions from 'types/Permissions.generated'
import { isUndefinedOrNull } from 'util/checks'
import { MetaPath } from 'util/metaPath'
import { meta as metaSoapState } from 'view/atendimentos/atendimento-individual/model'

import { isInicioOuContinuacaoPreNatal } from '../../../pre-natal/util-preNatal'
import { AgendarConsultaSection, AgendarConsultaSectionProps } from './AgendarConsultaSection'
import { ComprovanteAgendamentoSection } from './ComprovanteAgendamentoSection'
import {
  AgendarConsultaSectionModel,
  AgendarProximasConsultasSectionModel,
  ComprovanteAgendamentoSectionModel,
} from './model-agendarConsultaAtendimento'
import { ProximasConsultasPanel, ProximasConsultasPanelProps } from './ProximasConsultasPanel'

export interface AgendarConsultaPanelModel {
  avulsa?: AgendarConsultaSectionModel
  proximasConsultas?: AgendarProximasConsultasSectionModel
  openedAccordions?: (string | number)[]
  comprovante?: ComprovanteAgendamentoSectionModel
}

interface AgendarConsultaPanelProps
  extends Partial<Omit<ProximasConsultasPanelProps, 'name' | 'idadeGestacionalEmDias'>>,
    Partial<
      Pick<AgendarConsultaSectionProps, 'hasPainelLateral' | 'isRegistroTardio' | 'tipoPreNatal' | 'showHorarioFinal'>
    > {
  name: MetaPath<AgendarConsultaPanelModel>
  mostrarCheckboxesComprovantes: boolean
}

export const AgendarConsultaPanel = React.memo((props: AgendarConsultaPanelProps) => {
  const {
    name,
    isRegistroTardio,
    tipoPreNatal,
    dataAtendimento,
    prontuarioId,
    hasPainelLateral,
    mostrarCheckboxesComprovantes,
    showHorarioFinal = false,
  } = props

  const {
    input: { value: idadeGestacional },
  } = usePecField({ name: metaSoapState.acompanhamentoPreNatal.idadeGestacional, subscription: { value: true } })

  const idadeGestacionalEmDias = idadeGestacional?.ecograficaEmDias || idadeGestacional?.cronologicaEmDias

  const hasInfoToCalculateSugestoes =
    !isUndefinedOrNull(idadeGestacionalEmDias) &&
    !isUndefinedOrNull(dataAtendimento) &&
    !isUndefinedOrNull(prontuarioId)

  const shouldRenderProximasConsultas = hasInfoToCalculateSugestoes && isInicioOuContinuacaoPreNatal(tipoPreNatal)

  return (
    <CheckPermission permission={Permissions.visualizarAgenda.agendarEEditar} checkSupervisorPermission>
      <FormSectionAccordion
        name={name.openedAccordions}
        title='Agendar consulta'
        uuid='agendarConsulta'
        dangerouslySetExpanded={shouldRenderProximasConsultas}
      >
        <AgendarConsultaSection
          name={name.avulsa}
          errorPath={name.avulsa}
          isRegistroTardio={isRegistroTardio}
          hasPainelLateral={hasPainelLateral}
          showHorarioFinal={showHorarioFinal}
        />
        {shouldRenderProximasConsultas && (
          <Cell size={12}>
            <ProximasConsultasPanel
              name={name.proximasConsultas}
              idadeGestacionalEmDias={idadeGestacionalEmDias}
              dataAtendimento={dataAtendimento}
              prontuarioId={prontuarioId}
            />
          </Cell>
        )}
        {mostrarCheckboxesComprovantes && (
          <ComprovanteAgendamentoSection name={name.comprovante} maisDeUmComprovante={shouldRenderProximasConsultas} />
        )}
      </FormSectionAccordion>
    </CheckPermission>
  )
})
